import React from "react";
import { logoUrl } from "@/lib/api";
import { formatEUR, formatNum, formatDate, computeTotals } from "@/lib/invoice";

const Field = ({ children, className = "" }) =>
  children ? <div className={className}>{children}</div> : null;

const servicePeriod = (inv) => {
  const a = inv.service_date;
  const b = inv.service_date_end;
  if (a && b) return { label: "Leistungszeitraum", value: `${formatDate(a)} – ${formatDate(b)}` };
  if (a || b) return { label: "Leistungsdatum", value: formatDate(a || b) };
  return null;
};

export const InvoicePreview = ({ invoice }) => {
  if ((invoice.template || "modern") === "classic") return <ClassicPreview invoice={invoice} />;
  return <ModernPreview invoice={invoice} />;
};

// ---------------------------------------------------------------- MODERN
const ModernPreview = ({ invoice }) => {
  const s = invoice.sender || {};
  const r = invoice.recipient || {};
  const klein = invoice.is_kleinunternehmer;
  const totals = computeTotals(invoice.items, klein);
  const logo = s.logo_path ? logoUrl(s.logo_path) : null;

  const senderLine = [
    s.company || s.name,
    s.address_line1,
    [s.zip, s.city].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(" · ");

  const groupRows = Object.entries(totals.groups).sort((a, b) => Number(a[0]) - Number(b[0]));

  return (
    <div
      data-testid="invoice-preview"
      className="w-full max-w-[794px] min-h-[1123px] bg-white paper-shadow ring-1 ring-slate-200 shrink-0 flex flex-col animate-fade-in"
      style={{ padding: "56px 56px 40px" }}
    >
      <div className="flex justify-end min-h-[10px] mb-1">
        {logo && (
          <img src={logo} alt="Logo" data-testid="preview-logo" className="max-h-[64px] max-w-[220px] object-contain" />
        )}
      </div>

      <div className="text-[9px] text-slate-400 border-b border-slate-100 pb-1 mb-6">
        {senderLine || <span className="italic text-slate-300">Absenderzeile — bitte Briefkopf auswählen</span>}
      </div>

      <div className="flex justify-between gap-8 mb-14">
        <div className="text-[13px] text-slate-800 leading-relaxed">
          {r.company || r.name ? (
            <>
              {r.company && <div className="font-medium">{r.company}</div>}
              {r.name && <div>{r.name}</div>}
              {r.address_line1 && <div>{r.address_line1}</div>}
              {(r.zip || r.city) && <div>{[r.zip, r.city].filter(Boolean).join(" ")}</div>}
              {r.country && r.country !== "Deutschland" && <div>{r.country}</div>}
            </>
          ) : (
            <div className="italic text-slate-300">Empfängeradresse</div>
          )}
        </div>
        <div className="text-right text-[12px] shrink-0 space-y-1 min-w-[190px]">
          <Row label="Rechnungs-Nr." value={invoice.invoice_number || "—"} mono />
          <Row label="Rechnungsdatum" value={formatDate(invoice.invoice_date) || "—"} mono />
          {(() => {
            const sp = servicePeriod(invoice);
            return sp && <Row label={sp.label} value={sp.value} mono />;
          })()}
          {r.customer_number && <Row label="Kundennummer" value={r.customer_number} mono />}
          {invoice.due_date && <Row label="Fällig am" value={formatDate(invoice.due_date)} mono />}
        </div>
      </div>

      <h1 className="font-heading text-3xl font-extrabold text-slate-900 mb-4">
        Rechnung {invoice.invoice_number}
      </h1>

      <Field className="text-[12.5px] text-slate-700 whitespace-pre-line mb-6">{invoice.intro_text}</Field>

      <table className="w-full text-[12px] border-collapse">
        <thead>
          <tr className="bg-primary text-white">
            <th className="text-left font-semibold py-2 px-2 w-8">Pos.</th>
            <th className="text-left font-semibold py-2 px-2">Bezeichnung</th>
            <th className="text-right font-semibold py-2 px-2 w-14">Menge</th>
            <th className="text-left font-semibold py-2 px-2 w-14">Einh.</th>
            <th className="text-right font-semibold py-2 px-2 w-24">Einzelpreis</th>
            {!klein && <th className="text-right font-semibold py-2 px-2 w-14">MwSt.</th>}
            <th className="text-right font-semibold py-2 px-2 w-24">Gesamt</th>
          </tr>
        </thead>
        <tbody>
          {(invoice.items || []).length === 0 && (
            <tr>
              <td colSpan={klein ? 6 : 7} className="py-6 text-center text-slate-300 italic">
                Noch keine Positionen hinzugefügt
              </td>
            </tr>
          )}
          {(invoice.items || []).map((it, i) => {
            const line = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
            return (
              <tr key={i} className="border-b border-slate-100">
                <td className="py-2 px-2 text-center font-mono text-slate-500">{i + 1}</td>
                <td className="py-2 px-2 text-slate-800">{it.description || "—"}</td>
                <td className="py-2 px-2 text-right font-mono">{formatNum(it.quantity)}</td>
                <td className="py-2 px-2 text-slate-600">{it.unit}</td>
                <td className="py-2 px-2 text-right font-mono">{formatEUR(it.unit_price)}</td>
                {!klein && <td className="py-2 px-2 text-right font-mono text-slate-600">{formatNum(it.vat_rate)} %</td>}
                <td className="py-2 px-2 text-right font-mono font-medium">{formatEUR(line)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="flex justify-end mt-4">
        <div className="w-[280px] text-[12.5px]">
          <div className="flex justify-between py-1 text-slate-700">
            <span>Zwischensumme (netto)</span>
            <span className="font-mono">{formatEUR(totals.net)}</span>
          </div>
          {!klein &&
            groupRows.map(([rate, amount]) => (
              <div key={rate} className="flex justify-between py-1 text-slate-600">
                <span>zzgl. {formatNum(rate)} % MwSt.</span>
                <span className="font-mono">{formatEUR(amount)}</span>
              </div>
            ))}
          <div className="flex justify-between py-2 mt-1 border-y-2 border-primary font-bold text-slate-900">
            <span>Gesamtbetrag</span>
            <span className="font-mono">{formatEUR(totals.gross)}</span>
          </div>
        </div>
      </div>

      {klein && (
        <p className="text-[11px] text-slate-500 mt-6">
          Gemäß § 19 UStG wird keine Umsatzsteuer berechnet (Kleinunternehmerregelung).
        </p>
      )}

      <Field className="text-[12.5px] text-slate-700 whitespace-pre-line mt-8">{invoice.outro_text}</Field>

      {(() => {
        const prompt = invoice.payment_terms === "prompt";
        const lead = prompt
          ? "Zahlbar prompt und ohne Abzug."
          : invoice.due_date
          ? `Bitte überweisen Sie den Betrag bis zum ${formatDate(invoice.due_date)}.`
          : "";
        const bank = s.iban
          ? `Konto: ${s.bank_name || ""} · IBAN ${s.iban}${s.bic ? ` · BIC ${s.bic}` : ""}.`
          : "";
        if (!lead && !bank) return null;
        return <p className="text-[11px] text-slate-500 mt-4">{[lead, bank].filter(Boolean).join(" ")}</p>;
      })()}

      <div className="mt-auto pt-6">
        <div className="border-t border-slate-200 pt-3 grid grid-cols-4 gap-3 text-[8.5px] text-slate-400 leading-snug">
          <div>
            {(s.company || s.name) && <div>{s.company || s.name}</div>}
            {s.address_line1 && <div>{s.address_line1}</div>}
            {(s.zip || s.city) && <div>{[s.zip, s.city].filter(Boolean).join(" ")}</div>}
          </div>
          <div>
            {s.phone && <div>Tel: {s.phone}</div>}
            {s.email && <div>{s.email}</div>}
            {s.website && <div>{s.website}</div>}
          </div>
          <div>
            {s.tax_number && <div>St.-Nr.: {s.tax_number}</div>}
            {s.vat_id && <div>USt-IdNr.: {s.vat_id}</div>}
          </div>
          <div>
            {s.bank_name && <div>{s.bank_name}</div>}
            {s.iban && <div>IBAN: {s.iban}</div>}
            {s.bic && <div>BIC: {s.bic}</div>}
          </div>
        </div>
      </div>
    </div>
  );
};

// --------------------------------------------------------------- CLASSIC
const ClassicPreview = ({ invoice }) => {
  const s = invoice.sender || {};
  const r = invoice.recipient || {};
  const klein = invoice.is_kleinunternehmer;
  const totals = computeTotals(invoice.items, klein);
  const logo = s.logo_path ? logoUrl(s.logo_path) : null;
  const groupRows = Object.entries(totals.groups).sort((a, b) => Number(a[0]) - Number(b[0]));
  const brand = (s.company || s.name || "").toUpperCase();

  return (
    <div
      data-testid="invoice-preview"
      className="w-full max-w-[794px] min-h-[1123px] bg-white paper-shadow ring-1 ring-slate-200 shrink-0 flex flex-col animate-fade-in text-slate-900"
      style={{ padding: "64px 60px 48px", fontFamily: '"IBM Plex Sans", sans-serif' }}
    >
      {/* Header: brand + Kontaktdaten */}
      <div className="flex justify-between items-start gap-8 mb-10">
        <div>
          {logo ? (
            <img src={logo} alt="Logo" data-testid="preview-logo" className="max-h-[52px] max-w-[220px] object-contain" />
          ) : brand ? (
            <div className="font-bold text-xl tracking-tight">{brand}</div>
          ) : (
            <div className="text-sm italic text-slate-300">Firmenname / Logo</div>
          )}
        </div>
        <div className="text-right text-[11px] text-slate-600 leading-relaxed">
          <div className="font-bold text-slate-900 text-[12px] mb-0.5">Kontaktdaten</div>
          {s.address_line1 && <div>{s.address_line1}</div>}
          {(s.zip || s.city) && <div>{[s.zip, s.city].filter(Boolean).join(" ")}</div>}
          {s.phone && <div>{s.phone}</div>}
          {s.email && <div>{s.email}</div>}
          {s.website && <div>{s.website}</div>}
        </div>
      </div>

      {/* Recipient */}
      <div className="mb-10 text-[13px] leading-relaxed">
        <div className="font-bold mb-1">Rechnungsempfänger:</div>
        {r.company || r.name ? (
          <>
            {r.company && <div>{r.company}</div>}
            {r.name && <div>{r.name}</div>}
            {r.address_line1 && <div>{r.address_line1}</div>}
            {(r.zip || r.city) && <div>{[r.zip, r.city].filter(Boolean).join(" ")}</div>}
          </>
        ) : (
          <div className="italic text-slate-300">Empfängeradresse</div>
        )}
      </div>

      <h1 className="text-center font-bold text-2xl mb-4">Rechnung {invoice.invoice_number}</h1>

      <div className="text-[12px] mb-5 space-y-0.5">
        <div>
          <span className="font-semibold">Datum:</span> {formatDate(invoice.invoice_date) || "—"}
        </div>
        {(() => {
          const sp = servicePeriod(invoice);
          return (
            sp && (
              <div>
                <span className="font-semibold">{sp.label}:</span> {sp.value}
              </div>
            )
          );
        })()}
        {r.customer_number && (
          <div>
            <span className="font-semibold">Kundennummer:</span> {r.customer_number}
          </div>
        )}
        {invoice.payment_terms !== "prompt" && invoice.due_date && (
          <div>
            <span className="font-semibold">Fällig am:</span> {formatDate(invoice.due_date)}
          </div>
        )}
      </div>

      {invoice.intro_text && (
        <p className="text-[12.5px] italic text-slate-700 whitespace-pre-line mb-5">{invoice.intro_text}</p>
      )}

      <table className="w-full text-[12px] border-collapse">
        <thead>
          <tr className="border-b-2 border-slate-900">
            <th className="text-left font-bold uppercase py-1.5 pr-2 tracking-wide">Beschreibung</th>
            <th className="text-right font-bold uppercase py-1.5 px-2 tracking-wide w-24">Einzelpreis</th>
            <th className="text-right font-bold uppercase py-1.5 px-2 tracking-wide w-16">Menge</th>
            {!klein && <th className="text-right font-bold uppercase py-1.5 px-2 tracking-wide w-14">MwSt.</th>}
            <th className="text-right font-bold uppercase py-1.5 pl-2 tracking-wide w-24">Summe</th>
          </tr>
        </thead>
        <tbody>
          {(invoice.items || []).length === 0 && (
            <tr>
              <td colSpan={klein ? 4 : 5} className="py-6 text-center text-slate-300 italic">
                Noch keine Positionen
              </td>
            </tr>
          )}
          {(invoice.items || []).map((it, i) => {
            const line = (Number(it.quantity) || 0) * (Number(it.unit_price) || 0);
            return (
              <tr key={i} className="border-b border-slate-200">
                <td className="py-2 pr-2 align-top">{it.description || "—"}</td>
                <td className="py-2 px-2 text-right font-mono align-top">{formatEUR(it.unit_price)}</td>
                <td className="py-2 px-2 text-right font-mono align-top">{formatNum(it.quantity)}</td>
                {!klein && <td className="py-2 px-2 text-right font-mono align-top">{formatNum(it.vat_rate)} %</td>}
                <td className="py-2 pl-2 text-right font-mono align-top">{formatEUR(line)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      <div className="flex justify-end mt-3">
        <div className="w-[280px] text-[12.5px]">
          {!klein && (
            <>
              <div className="flex justify-between py-0.5">
                <span>Zwischensumme (netto)</span>
                <span className="font-mono">{formatEUR(totals.net)}</span>
              </div>
              {groupRows.map(([rate, amount]) => (
                <div key={rate} className="flex justify-between py-0.5">
                  <span>zzgl. {formatNum(rate)} % MwSt.</span>
                  <span className="font-mono">{formatEUR(amount)}</span>
                </div>
              ))}
            </>
          )}
          <div className="flex justify-between py-1.5 mt-1 border-t-2 border-slate-900 font-bold">
            <span>Gesamtbetrag</span>
            <span className="font-mono">{formatEUR(totals.gross)}</span>
          </div>
        </div>
      </div>

      {invoice.outro_text && (
        <p className="text-[12.5px] text-slate-700 whitespace-pre-line mt-6">{invoice.outro_text}</p>
      )}

      <div className="mt-auto pt-8 text-[11px] text-slate-600 leading-relaxed">
        {(() => {
          const prompt = invoice.payment_terms === "prompt";
          const lead = prompt
            ? "Zahlbar prompt und ohne Abzug."
            : invoice.due_date
            ? `Zahlbar bis zum ${formatDate(invoice.due_date)} ohne Abzug.`
            : "";
          return lead ? <p>{lead}</p> : null;
        })()}
        <p>Bei Überweisung tragen Sie bitte im Feld „Verwendungszweck“ die Rechnungsnummer ein.</p>
        {(s.bank_name || s.iban) && (
          <div className="mt-3">
            {s.bank_name && <div>Bankverbindung: {s.bank_name}</div>}
            {s.iban && <div className="font-bold text-[14px] text-slate-900">IBAN: {s.iban}</div>}
            {s.bic && <div>BIC: {s.bic}</div>}
          </div>
        )}
        {klein && <p className="mt-2">Kleinunternehmer gemäß § 19 UStG – es wird keine Umsatzsteuer berechnet.</p>}
        {(s.tax_number || s.vat_id) && (
          <p className="mt-1">
            {[s.tax_number && `St.-Nr.: ${s.tax_number}`, s.vat_id && `USt-IdNr.: ${s.vat_id}`]
              .filter(Boolean)
              .join(" · ")}
          </p>
        )}
      </div>
    </div>
  );
};

const Row = ({ label, value, mono }) => (
  <div className="flex justify-end gap-3">
    <span className="text-slate-400">{label}</span>
    <span className={mono ? "font-mono text-slate-800 font-medium" : "text-slate-800"}>{value}</span>
  </div>
);
